// BOLUS_NOTES-29 (2026-09-17): flag-gated client diagnostics for the web app.
//
// diag.log(category, event, detail) is a no-op unless the server flag says
// diagnostics are ON for this user/device. Events are buffered and flushed
// every 30 s or 50 events through the `log_diagnostics` action. Nothing
// sensitive is ever logged (the server also strips token/password keys).
import api from './api';

type Level = 'debug' | 'info' | 'warn' | 'error';
interface DiagEvent { ts: string; level: Level; category: string; event: string; detail?: Record<string, unknown> }

const DEVICE_KEY = 'bolusnotes_diag_device_id';
const FLUSH_MS = 30_000;
const FLUSH_AT = 50;

function deviceId(): string {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id = (crypto?.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now().toString(36)).slice(0, 64);
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    return 'web-unknown';
  }
}

class Diagnostics {
  enabled = false;
  private buffer: DiagEvent[] = [];
  private timer: number | null = null;
  private sessionId = Math.random().toString(36).slice(2, 12);
  readonly deviceId = deviceId();

  /** Ask the server whether diagnostics are on for this user/device. */
  async refreshFlag(): Promise<boolean> {
    try {
      const res = await api.post('/v1/app/bolus_notes/fn/bolusnotes_api', {
        action: 'get_diagnostics_flag', payload: { device_id: this.deviceId },
      });
      const flag = res.data?.data ?? res.data?.result ?? res.data;
      this.enabled = flag?.enabled === true;
    } catch {
      this.enabled = false;
    }
    if (!this.enabled) this.buffer = [];
    return this.enabled;
  }

  async setFlag(enabled: boolean, hours?: number): Promise<boolean> {
    const res = await api.post('/v1/app/bolus_notes/fn/bolusnotes_api', {
      action: 'set_diagnostics_flag', payload: { enabled, device_id: '*', hours },
    });
    const flag = res.data?.data ?? res.data?.result ?? res.data;
    this.enabled = flag?.enabled === true;
    return this.enabled;
  }

  log(category: string, event: string, detail?: Record<string, unknown>, level: Level = 'info') {
    if (!this.enabled) return;
    this.buffer.push({ ts: new Date().toISOString(), level, category, event, detail });
    if (this.buffer.length >= FLUSH_AT) void this.flush();
    else if (this.timer === null) this.timer = window.setTimeout(() => void this.flush(), FLUSH_MS);
  }

  async flush() {
    if (this.timer !== null) { window.clearTimeout(this.timer); this.timer = null; }
    if (!this.enabled || this.buffer.length === 0) return;
    const events = this.buffer.splice(0, FLUSH_AT);
    try {
      const res = await api.post('/v1/app/bolus_notes/fn/bolusnotes_api', {
        action: 'log_diagnostics',
        payload: {
          device_id: this.deviceId, platform: 'web', environment: 'dev',
          app_version: process.env.REACT_APP_VERSION || 'web', session_id: this.sessionId, events,
        },
      });
      const r = res.data?.data ?? res.data?.result ?? res.data;
      if (r?.enabled === false) { this.enabled = false; this.buffer = []; }
    } catch {
      // drop on the floor — diagnostics must never affect the app
    }
  }

  async list(params: Record<string, unknown> = {}) {
    const res = await api.post('/v1/app/bolus_notes/fn/bolusnotes_api', { action: 'list_diagnostics', payload: params });
    return res.data?.data ?? res.data?.result ?? res.data;
  }
}

export const diag = new Diagnostics();
